// Geometry and utility functions for the floor planner

/**
 * Snap a point to the nearest grid intersection
 */
export function snapToGrid(point, gridSize) {
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
}

/**
 * Snap a point to the nearest existing wall endpoint within a threshold
 * Returns the snapped point or the original point if no endpoint is close enough
 */
export function snapToEndpoint(point, walls, threshold = 6) {
  let closest = null;
  let minDist = threshold;

  for (const wall of walls) {
    const endpoints = [
      { x: wall.x1, y: wall.y1 },
      { x: wall.x2, y: wall.y2 },
    ];
    for (const ep of endpoints) {
      const dist = distance(point, ep);
      if (dist < minDist) {
        minDist = dist;
        closest = ep;
      }
    }
  }

  return closest || point;
}

/**
 * Distance between two points
 */
export function distance(a, b) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/**
 * Length of a wall segment
 */
export function wallLength(wall) {
  return distance({ x: wall.x1, y: wall.y1 }, { x: wall.x2, y: wall.y2 });
}

/**
 * Find the closest point on a line segment to a given point
 */
export function closestPointOnSegment(point, segStart, segEnd) {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) return { ...segStart };

  let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  return {
    x: segStart.x + t * dx,
    y: segStart.y + t * dy,
    t,
  };
}

/**
 * Find which wall (if any) a point is closest to, within a threshold
 */
export function findWallAtPoint(point, walls, threshold = 8) {
  let closest = null;
  let minDist = threshold;

  for (const wall of walls) {
    const cp = closestPointOnSegment(
      point,
      { x: wall.x1, y: wall.y1 },
      { x: wall.x2, y: wall.y2 }
    );
    const dist = distance(point, cp);
    if (dist < minDist) {
      minDist = dist;
      closest = { wall, point: cp, distance: dist };
    }
  }

  return closest;
}

/**
 * Convert inches to display string
 */
export function formatDimension(inches, unit = 'imperial') {
  if (unit === 'metric') {
    const cm = inches * 2.54;
    if (cm >= 100) {
      return `${(cm / 100).toFixed(2)}m`;
    }
    return `${Math.round(cm)}cm`;
  }
  const feet = Math.floor(inches / 12);
  const remainingInches = Math.round(inches % 12);
  if (feet === 0) return `${remainingInches}"`;
  if (remainingInches === 0) return `${feet}'`;
  return `${feet}'${remainingInches}"`;
}

/**
 * Calculate the area of a polygon defined by vertices (in square inches)
 * Uses the shoelace formula
 */
export function polygonArea(vertices) {
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }
  return Math.abs(area) / 2;
}

/**
 * Convert square inches to display string
 */
export function formatArea(squareInches, unit = 'imperial') {
  if (unit === 'metric') {
    const sqMeters = squareInches * 0.00064516;
    return `${sqMeters.toFixed(1)} m²`;
  }
  const sqFeet = squareInches / 144;
  return `${Math.round(sqFeet)} sq ft`;
}

/**
 * Generate a unique ID for floor plan elements
 */
export function generateId(prefix = 'fp') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

/**
 * Get the midpoint of a wall
 */
export function wallMidpoint(wall) {
  return {
    x: (wall.x1 + wall.x2) / 2,
    y: (wall.y1 + wall.y2) / 2,
  };
}

/**
 * Get the angle of a wall in radians
 */
export function wallAngle(wall) {
  return Math.atan2(wall.y2 - wall.y1, wall.x2 - wall.x1);
}

/**
 * Convert world coordinates to screen coordinates
 */
export function worldToScreen(worldPoint, viewport, scale) {
  return {
    x: (worldPoint.x * scale * viewport.zoom) + viewport.x,
    y: (worldPoint.y * scale * viewport.zoom) + viewport.y,
  };
}

/**
 * Convert screen coordinates to world coordinates
 */
export function screenToWorld(screenPoint, viewport, scale) {
  return {
    x: (screenPoint.x - viewport.x) / (scale * viewport.zoom),
    y: (screenPoint.y - viewport.y) / (scale * viewport.zoom),
  };
}

/**
 * Find closed loops (rooms) from a set of walls.
 * Uses adjacency graph + minimal cycle detection.
 * Returns arrays of vertex lists for each detected room.
 */
export function findRooms(walls) {
  if (walls.length < 3) return [];

  // Build adjacency graph from wall endpoints
  const EPSILON = 0.5;
  const vertices = [];
  const edges = [];

  function findOrAddVertex(point) {
    for (let i = 0; i < vertices.length; i++) {
      if (distance(vertices[i], point) < EPSILON) return i;
    }
    vertices.push({ x: point.x, y: point.y });
    return vertices.length - 1;
  }

  for (const wall of walls) {
    const v1 = findOrAddVertex({ x: wall.x1, y: wall.y1 });
    const v2 = findOrAddVertex({ x: wall.x2, y: wall.y2 });
    if (v1 !== v2) {
      edges.push({ v1, v2, wallId: wall.id });
    }
  }

  // Build adjacency list
  const adj = vertices.map(() => []);
  for (const edge of edges) {
    adj[edge.v1].push(edge.v2);
    adj[edge.v2].push(edge.v1);
  }

  // Find minimal cycles using planar face detection
  // Sort adjacency lists by angle for consistent traversal
  for (let i = 0; i < vertices.length; i++) {
    adj[i].sort((a, b) => {
      const angleA = Math.atan2(vertices[a].y - vertices[i].y, vertices[a].x - vertices[i].x);
      const angleB = Math.atan2(vertices[b].y - vertices[i].y, vertices[b].x - vertices[i].x);
      return angleA - angleB;
    });
  }

  const usedEdges = new Set();
  const rooms = [];

  function nextVertex(from, to) {
    const neighbors = adj[to];
    const incomingAngle = Math.atan2(vertices[from].y - vertices[to].y, vertices[from].x - vertices[to].x);

    // Find the next edge in clockwise order
    let bestIdx = -1;
    let bestAngle = Infinity;

    for (let i = 0; i < neighbors.length; i++) {
      if (neighbors[i] === from && neighbors.length > 1) continue;
      let angle = Math.atan2(vertices[neighbors[i]].y - vertices[to].y, vertices[neighbors[i]].x - vertices[to].x);
      let diff = angle - incomingAngle;
      if (diff <= 0) diff += 2 * Math.PI;
      if (diff < bestAngle) {
        bestAngle = diff;
        bestIdx = i;
      }
    }

    return bestIdx >= 0 ? neighbors[bestIdx] : -1;
  }

  for (const edge of edges) {
    for (const [start, end] of [[edge.v1, edge.v2], [edge.v2, edge.v1]]) {
      const edgeKey = `${start}-${end}`;
      if (usedEdges.has(edgeKey)) continue;

      const path = [start, end];
      usedEdges.add(edgeKey);
      let current = end;
      let prev = start;
      let valid = true;

      for (let step = 0; step < vertices.length; step++) {
        const next = nextVertex(prev, current);
        if (next === -1) { valid = false; break; }

        const nextEdgeKey = `${current}-${next}`;
        usedEdges.add(nextEdgeKey);

        if (next === start) break; // closed loop

        if (path.includes(next)) { valid = false; break; }
        path.push(next);
        prev = current;
        current = next;
      }

      if (valid && path.length >= 3 && current !== -1) {
        const roomVertices = path.map(i => vertices[i]);
        const area = polygonArea(roomVertices);
        if (area > 100) { // minimum area threshold (100 sq inches)
          rooms.push({
            vertices: roomVertices,
            area,
            vertexIndices: path,
          });
        }
      }
    }
  }

  // Deduplicate rooms (same vertices in different order)
  const unique = [];
  for (const room of rooms) {
    const key = [...room.vertexIndices].sort().join(',');
    if (!unique.find(r => [...r.vertexIndices].sort().join(',') === key)) {
      unique.push(room);
    }
  }

  return unique;
}
